import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AdminNotificationProvider, useAdminNotifications } from '@/context/AdminNotificationContext';
import { 
  LayoutDashboard, 
  Users, 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  Settings, 
  Bell, 
  LogOut, 
  Menu,
  X,
  Shield,
  ChevronDown,
  TrendingUp,
  ClipboardList,
  Activity,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/deposits', icon: ArrowDownToLine, label: 'Deposits' },
  { path: '/admin/withdrawals', icon: ArrowUpFromLine, label: 'Withdrawals' },
  { path: '/admin/profits', icon: TrendingUp, label: 'Profits' },
  { path: '/admin/activity-feed', icon: Activity, label: 'Activity Feed' },
  { path: '/admin/audit-log', icon: ClipboardList, label: 'Audit Log' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
];

function AdminLayoutInner() {
  const { admin, adminLogout } = useAuth();
  const { wsConnected, unreadCount, permissionGranted, requestPermission, clearUnreadCount } = useAdminNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Request notification permission on mount
  useEffect(() => {
    if (!permissionGranted && 'Notification' in window && Notification.permission === 'default') {
      // Small delay to avoid immediate popup
      const timer = setTimeout(() => {
        requestPermission();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [permissionGranted, requestPermission]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Clear unread count when visiting activity feed
  useEffect(() => {
    if (location.pathname === '/admin/activity-feed') {
      clearUnreadCount();
    }
  }, [location.pathname, clearUnreadCount]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin-login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const NavItem = ({ item, onClick }) => {
    const isActivityFeed = item.path === '/admin/activity-feed';
    return (
      <NavLink
        to={item.path}
        end={item.end}
        onClick={onClick}
        className={({ isActive }) =>
          `sidebar-link ${isActive ? 'active' : ''}`
        }
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        {isActivityFeed && unreadCount > 0 && (
          <span className="ml-auto bg-hotpink-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col z-30">
        <div className="flex flex-col flex-1 glass-card m-4 p-4">
          {/* Logo */}
          <div className="flex items-center gap-2 px-4 py-4 mb-2">
            <Shield className="w-8 h-8 text-hotpink-400" />
            <span className="text-2xl font-outfit font-bold gradient-text-pink">Admin</span>
          </div>
          <p className="text-xs text-white/50 px-4 mb-6">Dexfolio Control Panel</p>

          {/* Navigation */}
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>

          {/* Connection Status */}
          <div className="border-t border-white/10 pt-4 mt-4 px-4">
            <div className="flex items-center gap-2 text-xs">
              {wsConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">Live Updates Active</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-yellow-400" />
                  <span className="text-yellow-400">Reconnecting...</span>
                </>
              )}
            </div>
          </div>

          {/* Admin Info */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="w-10 h-10 rounded-full bg-hotpink-400/20 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-hotpink-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{admin?.username}</p>
                <p className="text-xs text-white/50">Administrator</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 glass-card mx-4 mt-4 rounded-xl">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="admin-mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>

            {/* Page Title - Mobile */}
            <div className="lg:hidden flex-1 text-center">
              <p className="text-sm font-semibold text-white">Admin Panel</p>
            </div>

            {/* Desktop: Connection Status */}
            <div className="hidden lg:flex items-center gap-2">
              {wsConnected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs font-medium">Live</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <span className="w-2 h-2 rounded-full bg-yellow-400" />
                  <span className="text-yellow-400 text-xs font-medium">Connecting...</span>
                </div>
              )}
            </div>

            {/* Spacer */}
            <div className="hidden lg:block flex-1" />

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* Activity Feed Button with Badge */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => navigate('/admin/activity-feed')}
                data-testid="activity-feed-btn"
              >
                <Activity className="w-5 h-5 text-white/70" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-hotpink-400 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>

              {/* Admin Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="admin-dropdown-btn">
                    <div className="w-8 h-8 rounded-full bg-hotpink-400/20 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-hotpink-400" />
                    </div>
                    <span className="hidden sm:inline text-sm">{admin?.username}</span>
                    <ChevronDown className="w-4 h-4 text-white/50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-space-100 border-white/10">
                  <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="admin-settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="text-hotpink-400" data-testid="admin-logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={closeMobileMenu}
              data-testid="admin-mobile-menu-backdrop"
            />
            
            {/* Menu Panel */}
            <div
              className="fixed top-0 left-0 bottom-0 w-64 glass-card z-50 lg:hidden p-4 overflow-y-auto"
              data-testid="admin-mobile-menu-panel"
            >
              {/* Close Button */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-8 h-8 text-hotpink-400" />
                  <span className="text-xl font-outfit font-bold gradient-text-pink">Admin</span>
                </div>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Close menu"
                  data-testid="close-admin-mobile-menu-btn"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <p className="text-xs text-white/50 px-4 mb-6">Dexfolio Control Panel</p>
              
              {/* Navigation */}
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <NavItem key={item.path} item={item} onClick={closeMobileMenu} />
                ))}
              </nav>

              {/* Mobile Connection Status */}
              <div className="border-t border-white/10 pt-4 mt-4 px-4">
                <div className="flex items-center gap-2 text-xs">
                  {wsConnected ? (
                    <>
                      <Wifi className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-400">Live Updates Active</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3 text-yellow-400" />
                      <span className="text-yellow-400">Reconnecting...</span>
                    </>
                  )}
                </div>
              </div>

              {/* Mobile Admin Info */}
              <div className="border-t border-white/10 pt-4 mt-6">
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-10 h-10 rounded-full bg-hotpink-400/20 flex items-center justify-center shrink-0">
                    <Shield className="w-5 h-5 text-hotpink-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{admin?.username}</p>
                    <p className="text-xs text-white/50">Administrator</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-hotpink-400 hover:bg-hotpink-400/10 justify-start"
                  onClick={() => { handleLogout(); closeMobileMenu(); }}
                  data-testid="admin-mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminNotificationProvider>
      <AdminLayoutInner />
    </AdminNotificationProvider>
  );
}
